import React, { useEffect, useRef } from 'react'
import { Controller } from 'react-hook-form'
import type { Control, FieldValues } from 'react-hook-form'

interface RangeProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'name'> {
  control: Control<FieldValues>
  name: string
  min: number
  max: number
}

// Portion of the track that reads as "filled" (0-100%), used by the WebKit track
// gradient in Range.css. Firefox paints ::-moz-range-progress for free.
const fillPct = (v: number | string | undefined, min: number, max: number) => {
  const pct = ((Number(v ?? min) - min) / (max - min)) * 100
  return `${Math.max(0, Math.min(100, Number.isFinite(pct) ? pct : 0))}%`
}

// The slider input, split out so it can hold a ref + effect (a Controller render
// prop can't). Uncontrolled (defaultValue) so the thumb tracks the pointer
// smoothly during its own drag; but when the field value changes from elsewhere
// (e.g. dragging the block on the chart) and this slider isn't the one being
// dragged, we reflect that value onto the thumb imperatively - the best of both.
const RangeInput = ({
  value, onChange, min, max, className, name,
}: {
  value: number | string | undefined
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  min: number
  max: number
  className?: string
  name: string
}) => {
  const ref = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el || document.activeElement === el) return
    const next = String(value ?? min)
    if (el.value !== next) {
      el.value = next
      el.style.setProperty('--range-pct', fillPct(value, min, max))
    }
  }, [value, min, max])

  return (
    <input
      ref={ref}
      className={['tm-range', className].filter(Boolean).join(' ')}
      style={{ '--range-pct': fillPct(value, min, max) } as React.CSSProperties}
      onChange={(e) => {
        e.currentTarget.style.setProperty('--range-pct', fillPct(e.currentTarget.value, min, max))
        onChange(e)
      }}
      defaultValue={value ?? min}
      type="range"
      id={name}
      name={name}
      min={min}
      max={max}
    />
  )
}

const Range = ({ control, name, min, max, defaultValue: _defaultValue, className }: RangeProps) => (
  <Controller
    control={control}
    name={name}
    render={({ field: { onChange, value } }) => (
      <div className="flex flex-col w-full">
        <RangeInput value={value} onChange={onChange} min={min} max={max} className={className} name={name} />
        <div className="w-full flex justify-between pt-1">
          <span className="text-xs text-gray-400 tabular-nums">{min}</span>
          <span className="text-xs text-gray-400 tabular-nums">{max}</span>
        </div>
      </div>
    )}
  />
)

export default Range
